// @ is an alias to /src
import mastertime from '@/api/mastertime'

export const namespaced = true

export const state = {
  customers: [],
  customerAdmins: [],
  customerFeatures: [],
  customerUsageReportSummary: [],
  customer: {},
  customerWebhook: {},
  customerWebhookSubscription: [],
}

export const mutations = {
  setCustomers(state, customers) {
    state.customers = customers
  },

  setCustomerAdmins(state, customerAdmins) {
    state.customerAdmins = customerAdmins
  },

  setCustomerFeatures(state, customerFeatures) {
    state.customerFeatures = customerFeatures
  },

  setCustomerUsageReportSummary(state, customerUsageReportSummary) {
    state.customerUsageReportSummary = customerUsageReportSummary
  },

  setCustomer(state, customer) {
    state.customer = customer
  },

  setCustomerWebhook(state, customerWebhook) {
    state.customerWebhook = customerWebhook
  },

  setCustomerWebhookSubscription(state, customerWebhookSubscription) {
    state.customerWebhookSubscription = customerWebhookSubscription
  },

  addCustomer(state, customer) {
    state.customers.push(customer)
  },

  editCustomer(state, customer) {
    const index = state.customers.findIndex(
      (element) => element.company_uuid === customer.company_uuid
    )
    if (index !== -1) {
      state.customers.splice(index, 1, customer)
    }
  },

  addCustomerAdmin(state, customerAdmin) {
    state.customerAdmins.push(customerAdmin)
  },

  editCustomerAdmin(state, customerAdmin) {
    const index = state.customerAdmins.findIndex(
      (element) =>
        element.company_admin_uuid === customerAdmin.company_admin_uuid
    )
    if (index !== -1) {
      state.customerAdmins.splice(index, 1, customerAdmin)
    }
  },

  deleteCustomerAdmin(state, uuid) {
    const index = state.customerAdmins.findIndex(
      (el) => el.company_admin_uuid === uuid
    )
    if (index !== -1) {
      state.customerAdmins.splice(index, 1)
    }
  },

  addCustomerFeature(state, customerFeature) {
    state.customerFeatures.push(customerFeature)
  },

  deleteCustomerFeature(state, uuid) {
    const index = state.customerFeatures.findIndex(
      (el) => el.company_feature_toggle_uuid === uuid
    )
    if (index !== -1) {
      state.customerFeatures.splice(index, 1)
    }
  },

  editCustomerFeature(state, customerFeature) {
    const index = state.customerFeatures.findIndex(
      (el) =>
        el.company_feature_toggle_uuid ===
        customerFeature.company_feature_toggle_uuid
    )
    if (index !== -1) {
      state.customerFeatures.splice(index, 1, customerFeature)
    }
  },
}

export const actions = {
  async fetchCustomers({ commit }) {
    const { data } = await mastertime.fetchCustomers()
    const customers = data.data

    commit('setCustomers', customers)
  },

  async fetchCustomerAdmins({ commit }, uuid) {
    const { data } = await mastertime.fetchCustomerAdmins(uuid)
    const customerAdmins = data.data

    commit('setCustomerAdmins', customerAdmins)
  },

  async fetchCustomerFeatures({ commit }, uuid) {
    const { data } = await mastertime.fetchCustomerFeatures(uuid)
    const customerFeatures = data.data

    commit('setCustomerFeatures', customerFeatures)
  },

  async fetchCustomerUsageReportSummary({ commit }, payload) {
    const { data } = await mastertime.fetchCustomerUsageReportSummary(payload)
    const customerUsageReportSummary = data.data

    commit('setCustomerUsageReportSummary', customerUsageReportSummary)
  },

  async fetchCustomer({ commit, getters, state }, uuid) {
    if (state.customer.company_uuid === uuid) {
      return state.customer
    }

    const customer = getters.getCustomerByUuid(uuid)

    if (customer) {
      commit('setCustomer', customer)
      return customer
    } else {
      const { data } = await mastertime.fetchCustomer(uuid)
      const customer = data

      commit('setCustomer', customer)
      return customer
    }
  },

  async fetchCustomerWebhook({ commit }, uuid) {
    const { data } = await mastertime.fetchCustomerWebhook(uuid)
    const customerWebhook = data

    commit('setCustomerWebhook', customerWebhook)
  },

  async fetchCustomerWebhookSubscription({ commit }, payload) {
    const { data } = await mastertime.fetchCustomerWebhookSubscription(payload)
    const customerWebhookSubscription = data.data

    commit('setCustomerWebhookSubscription', customerWebhookSubscription)
  },

  async addCustomer({ commit }, customer) {
    const { data } = await mastertime.addCustomer(customer)
    const newCustomer = data

    commit('addCustomer', newCustomer)
  },

  async editCustomer({ commit }, customer) {
    const { data } = await mastertime.editCustomer(customer)
    const customerUpdate = data

    commit('setCustomer', customerUpdate)
    commit('editCustomer', customerUpdate)
  },

  async configCustomerWebhook({ commit }, payload) {
    const { data } = await mastertime.configCustomerWebhook(payload)
    const customerWebhookUpdate = data

    commit('setCustomerWebhook', customerWebhookUpdate)
  },

  async editCustomerConfigActivate({ commit, state }, customerUpdate) {
    const { data } = await mastertime.editCustomerConfigActivate(customerUpdate)

    if (state.customer.company_uuid === data.company_uuid) {
      const customerUpdated = {
        ...state.customer,
        activate_end_date: data.activate_end_date,
        activate_start_date: data.activate_start_date,
        activate_timezone: data.activate_timezone,
        company_uuid: data.company_uuid,
        max_employee: data.max_employee,
        note: data.note,
      }

      commit('setCustomer', customerUpdated)
      commit('editCustomer', customerUpdated)
      return
    }
  },

  async addCustomerAdmin({ commit }, payload) {
    const { data } = await mastertime.addCustomerAdmin(payload)
    const newCustomerAdmin = data

    commit('addCustomerAdmin', newCustomerAdmin)
  },

  async editCustomerAdmin({ commit }, payload) {
    const { data } = await mastertime.editCustomerAdmin(payload)
    const customerAdminUpdate = data

    commit('editCustomerAdmin', customerAdminUpdate)
  },

  async deleteCustomerAdmin({ commit }, payload) {
    await mastertime.deleteCustomerAdmin(payload)

    commit('deleteCustomerAdmin', payload.company_admin_uuid)
  },

  async addCustomerFeature({ commit }, customerFeature) {
    const { data } = await mastertime.addCustomerFeature(customerFeature)
    const newCustomerFeature = data

    commit('addCustomerFeature', newCustomerFeature)
  },

  async editCustomerFeature({ commit }, customerFeature) {
    const { data } = await mastertime.editCustomerFeature(customerFeature)
    const customerFeatureUpdate = data

    commit('editCustomerFeature', customerFeatureUpdate)
  },

  async deleteCustomerFeature({ commit }, uuid) {
    await mastertime.deleteCustomerFeature(uuid)

    commit('deleteCustomerFeature', uuid)
  },

  async generateFile(_, payload) {
    const { data } = await mastertime.generateFile(payload)

    return data
  },

  async generateFileProgress(_, job_process_uuid) {
    const { data } = await mastertime.generateFileProgress(job_process_uuid)

    return data
  },

  async sendEmailToCompanyAboutToExpire(_, payload) {
    const { data } = await mastertime.sendEmailToCompanyAboutToExpire(payload)

    return data
  },
}

export const getters = {
  getCustomerByUuid: (state) => (uuid) => {
    return state.customers.find((customer) => customer.company_uuid === uuid)
  },
}
